import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Header,
  HeaderRow,
  HeaderCell,
  Body,
  Row,
  Cell,
} from "@table-library/react-table-library/table";
import { FiChevronUp } from "react-icons/fi";
import { useTheme } from "@table-library/react-table-library/theme";
import { getTheme } from "@table-library/react-table-library/baseline";

export function TemplateDataTable({
  columns,
  fetchData,
  flags = {
    search: true,
    sorting: true,
    pagination: true,
    skeleton: true,
    selectable: false,
  },
  pageSize = 5,
  onRowClick,
  onSelectionChange,
}) {
  const params = new URLSearchParams(window.location.search);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(Number(params.get("page")) || 1);
  const [search, setSearch] = useState(params.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [sort, setSort] = useState({
    key: params.get("sortKey") || "",
    order: params.get("sortOrder") || "asc",
  });
  const [loading, setLoading] = useState(false);
  const [activeFilterColumns, setActiveFilterColumns] = useState({});


  const [selectedRowIds, setSelectedRowIds] = useState(new Set());

  /* Debounce search */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

 useEffect(() => {
  let isMounted = true;

  setLoading(true);

  fetchData({
    page,
    limit: pageSize,
    search: debouncedSearch,
    sort,
    activeFilters: buildBackendFilters(),
  })
    .then((r) => {
      if (!isMounted) return;
      setRows(r.data);
      setTotal(r.total);
      setSelectedRowIds(new Set());
    })
    .finally(() => {
      if (isMounted) setLoading(false);
    });

  return () => {
    isMounted = false;
  };
}, [page, debouncedSearch, sort, pageSize, fetchData, activeFilterColumns]);

  /* Sync URL */
  useEffect(() => {
    const p = new URLSearchParams();
    if (page > 1) p.set("page", page);
    if (search) p.set("search", search);
    if (sort.key) {
      p.set("sortKey", sort.key);
      p.set("sortOrder", sort.order);
    }
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${p.toString()}`
    );
  }, [page, search, sort]);

  const buildBackendFilters = () => {
    const result = {};

    columns.forEach((col) => {
      if (!col.filterable) return;

      const backendKey = col.backendFilterKey;
      const valueKey = col.filterValueKey;

      if (activeFilterColumns[backendKey]) {
        result[valueKey] = true; // value is resolved in parent
      }
    });

    return result;
  };


  /* Fetch data */
  
  const data = useMemo(() => ({ nodes: rows }), [rows]);

  /* Selection helpers */
  const toggleRow = (id) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRowIds.size === rows.length) {
      setSelectedRowIds(new Set());
    } else {
      setSelectedRowIds(new Set(rows.map((r) => r.id)));
    }
  };


  useEffect(() => {
    if (!onSelectionChange) return;
    onSelectionChange(rows.filter((r) => selectedRowIds.has(r.id)));
  }, [selectedRowIds, rows, onSelectionChange]);

  /* Theme */
  const theme = useTheme([
    getTheme(),
    {
      Table: `
        --data-table-library_grid-template-columns:
          ${flags.selectable ? "24px " : ""}
          ${columns.map(() => "1fr").join(" ")};
      `,
      HeaderRow: `
        background-color: #F3F4F6 !important;
        border-bottom: 1px solid #E5E7EB;
      `,
      HeaderCell: `
        background-color: #F3F4F6 !important;
        padding: 16px 18px;
        font-size: 11px;
        font-weight: 600;
        color: #6B7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      `,
      Row: `
        border-bottom: 1px solid #E5E7EB;
        transition: background-color 0.12s ease;

        &:hover {
          background-color: #F9FAFB;
        }
      `,
      Cell: `
        padding: 14px 16px;
        font-size: 11px;
        color: #111827;
      `,
    },
  ]);

  const toggleSort = (key) => {
    if (!flags.sorting) return;
    setPage(1);
    setSort((s) => ({
      key,
      order: s.key === key && s.order === "asc" ? "desc" : "asc",
    }));
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
      {flags.search && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <input
            placeholder="Search templates"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            style={{
              border: "1px solid #E5E7EB",
              borderRadius: 8,
              padding: "8px 12px",
              width: 240,
              fontSize: 11,
            }}
          />
        </div>
      )}

      <Table data={data} theme={theme} layout={{ custom: true }}>
        {() => (
          <>
            <Header>
              <HeaderRow>
                {flags.selectable && (
                  <HeaderCell
                    style={{
                      padding: 0,
                      width: 24,
                      textAlign: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && selectedRowIds.size === rows.length}
                      onChange={toggleAll}
                      style={{
                        margin: 0,
                        width: 14,
                        height: 14,
                        cursor: "pointer",
                        opacity: 0.85,
                      }}
                    />
                  </HeaderCell>
                )}

                {columns.map((col) => (
                  <HeaderCell
                    key={col.key}
                    style={{
                      cursor: col.sortable || col.filterable ? "pointer" : "default",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {/* Sort label */}
                    <span onClick={() => col.sortable && toggleSort(col.key)}>
                      {col.label}
                    </span>

                    {/* Filter arrow */}
                    {/* {col.filterable && (
                     <FiChevronUp
  onClick={(e) => {
    e.stopPropagation();
    setPage(1);
    setActiveFilterColumns((prev) => ({
      ...prev,
      [col.backendFilterKey]: !prev[col.backendFilterKey],
    }));
  }}
  style={{
    fontSize: 14,
    opacity: activeFilterColumns[col.backendFilterKey] ? 1 : 0.35,
    cursor: "pointer",
    transform: activeFilterColumns[col.backendFilterKey]
      ? "rotate(0deg)"   // filter ON
      : "rotate(180deg)", // filter OFF
    transition: "transform 0.15s ease, opacity 0.15s ease",
  }}
/>

                    )} */}
                  </HeaderCell>

                ))}
              </HeaderRow>
            </Header>

            <Body>
              {loading && flags.skeleton ? (
                Array.from({ length: pageSize }).map((_, i) => (
                  <Row key={i} item={{}}>
                    <Cell colSpan={flags.selectable ? columns.length + 1 : columns.length}>
                      <div style={{ height: 48, background: "#F3F4F6", borderRadius: 8 }} />
                    </Cell>
                  </Row>
                ))
              ) : (
                rows.map((row) => (
                  <Row
                    key={row.id}
                    item={row}
                    onClick={() => onRowClick?.(row)}
                    style={{
                      cursor: onRowClick ? "pointer" : "default",
                      backgroundColor: selectedRowIds.has(row.id) ? "#F9FAFB" : undefined,
                    }}
                  >
                    {flags.selectable && (
                      <Cell
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          padding: 0,
                          width: 24,
                          textAlign: "center",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRowIds.has(row.id)}
                          onChange={() => toggleRow(row.id)}
                          style={{
                            margin: 0,
                            width: 14,
                            height: 14,
                            cursor: "pointer",
                            opacity: 0.85,
                          }}
                        />
                      </Cell>
                    )}

                    {columns.map((col) => (
                      <Cell key={col.key}>
                        {col.render ? col.render(row) : row[col.key]}
                      </Cell>
                    ))}
                  </Row>
                ))
              )}
            </Body>
          </>
        )}
      </Table>

      {flags.pagination && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: 16,
            alignItems: "center",
            fontSize: 12,
            color: "#6B7280",
          }}
        >
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              border: "1px solid #D1D5DB",
              background: page === 1 ? "#F9FAFB" : "#FFFFFF",
              cursor: page === 1 ? "not-allowed" : "pointer",
            }}
          >
            Prev
          </button>

          <span>
            Page {page} / {totalPages}
          </span>

          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              border: "1px solid #D1D5DB",
              background: page === totalPages ? "#F9FAFB" : "#FFFFFF",
              cursor: page === totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
